import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import { AssignmentListItem } from 'app/models/assignment-list-params'
import { loadCurrent } from 'app/state/utils/generation'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import {
  assignmentListLoadFailed,
  getAssignmentList,
  startLoadingAssignmentList,
  stopLoadingAssignmentList,
} from './actions'
import { AssignmentListState } from './reducer'

type GetState = () => { assignmentList: AssignmentListState }

/** One request at a time: a strict-mode double mount, or a page effect firing during a load, joins it. */
let inFlight: Promise<unknown> | null = null

/**
 * ONE request for the whole Trade Closing grid (ADR_LOS-030):
 * `GET /api/assignment-list` returns the rows already joined, so the page no
 * longer loads the three assignment states plus every deal, facility and
 * entity in the tenant to resolve six columns.
 *
 * The response carries the store's generation at the time of the request; if
 * a write bumped it meanwhile the reducer drops the rows and this reads once
 * more (utils/generation.ts). On failure the store keeps what it had, the user
 * is told, and the loader still stops.
 */
export const loadAssignmentList = () => (dispatch: Dispatch, getState?: GetState) => {
  if (inFlight) return inFlight
  const generation = () => getState?.().assignmentList.generation ?? 0
  inFlight = loadCurrent(generation, g => load(g, dispatch)).finally(() => {
    // in flight is cleared BEFORE the loader stops: the grid's "reload once
    // while stale" effect fires on the loader stopping, and must start a new
    // read rather than join this finished one
    inFlight = null
    dispatch(stopLoadingAssignmentList())
  })
  return inFlight
}

const load = async (generation: number, dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  try {
    dispatch(startLoadingAssignmentList())
    const response = await client.get(`${apiServerUrl}/api/assignment-list`)
    return dispatch(getAssignmentList(response.data as AssignmentListItem[], generation))
  } catch {
    dispatch(assignmentListLoadFailed())
    ErrorNotification({
      title: "Couldn't load the assignments",
      message: 'The list may be empty or out of date. Refresh the page to try again.',
    })
  }
}
